import PdfPreview from '@components/Customization/PdfPreview/PdfPreview';
import { toPreviewAnswer } from '@components/Customization/PdfPreview/previewAnswer';
import PreviewSource from '@components/Customization/PdfPreview/PreviewSource';
import { usePreviewSource } from '@components/Customization/PdfPreview/usePreviewSource';
import { segmentedStyles, toolbarStyles } from '@components/Customization/PoiDesigner/designer.styles';
import DesignDialog from '@components/Customization/ReportDesigns/DesignDialog';
import { DndContext } from '@dnd-kit/core';
import {
  addPage, blockStep, copyOfBlock, designWrites, draftIssues, draftOf, emptied, insertBlock, isNamingIssue, locateBlock,
  locatePage, moveBlockTo, movePage, newPage, normalizeContent, pageForNewBlock, removeBlock, removePage, targetExists,
  toReportDesignBody, type DraftTarget, type ReportDesignDraft,
} from '@lib/customization/reportDesign';
import { keptDraftsOf, useAskBeforeLeaving } from '@lib/customization/unsavedWork';
import { useWriteChain } from '@lib/customization/useWriteChain';
import { useLocalization } from '@lib/localization';
import { Box, Button, ButtonBase, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { buttonStyles } from '@pages/CustomPoiTypes.styles';
import {
  isCurrentCopy, missingImagesOf, reportDesignLimits, STALE_VERSION, usePreviewReportDesignMutation,
  useUpdateReportDesignMutation, useUploadReportDesignImageMutation, type ReportBlock, type ReportBlockType,
  type ReportDesign,
} from '@store/api/reportDesignEndpoints';
import { isServerError } from '@store/error';
import { usePermission } from '@store/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EraserIcon, ExclamationTriangleIcon, LockIcon } from 'react-line-awesome';
import { createBlock } from './blockCatalog';
import BlockPalette from './canvas/BlockPalette';
import DesignCanvas, { type CanvasActions, type Shown } from './canvas/DesignCanvas';
import { dropOn, type DragSource, type Placement } from './canvas/dragging';
import { useBlockDrag } from './canvas/useBlockDrag';
import { useHandover, type Handover } from './canvas/useReveal';
import {
  bannerStyles, conflictBannerStyles, issueLineStyles, issueListStyles, layoutStyles, leftCellStyles, pageCellStyles,
  settingsCellStyles, viewBarStyles, viewBodyStyles, viewHiddenStyles, viewStyles,
} from './designer.styles';
import { issueWords, pageLabel, targetLabel } from './describe';
import { type DesignImages } from './ImageField';
import Inspector, { type InspectorActions } from './Inspector';
import StructurePanel, { type StructureActions } from './StructurePanel';

interface Props {
  /**
   * The design as it was last read. A newer copy replaces the draft only while nothing in it
   * is unsaved, and a copy older than what this editor has saved itself is never taken.
   */
  design: ReportDesign;
  canEdit: boolean;
  /**
   * Reads the stored design again, at once, and answers with the copy that leaves: the one
   * read, or the one held before where the read failed. For a Discard that has no copy of
   * the design as it is now to go back to.
   */
  reread: () => PromiseLike<{ data?: ReportDesign }>;
  /**
   * Said in place of the read-only line once the server has answered that the design is
   * gone, or no longer the reader's to open, while it was open here. The page then passes
   * `canEdit` as false: what the editor holds stays on screen to be read, and nothing of it
   * is sent any more.
   */
  closed?: string;
  /**
   * Starts a copy of the stored design under an owner the reader may create for. Offered to a
   * reader who may not change this one, and left out where they have nowhere to put a copy.
   */
  onCopy?: () => void;
}

/** The saved copy a draft started from: what it comes to as a request, and the version a save claims. */
interface Base {
  json: string;
  version: number;
}

/**
 * Unsaved drafts by design id, for as long as the tab lives. The designer unmounts on any
 * in-app navigation and the router cannot ask first, so without this a click on the sidebar
 * threw the work away; with it the draft is there again when the design is reopened. A kept
 * draft keeps the version it started from, so a design saved elsewhere meanwhile is a
 * conflict, as it is while the designer stays open. They are their author's alone: signing
 * out does not reload the tab, so whoever signs in next starts with none. The designs list
 * marks a design that has one, and a reload or a close of the tab asks first while one is kept.
 */
const keptDrafts = keptDraftsOf<{ draft: ReportDesignDraft; base: Base }>('report-design');

/** A draft as the request that would save it, as text: two drafts with the same text are one design. */
const signatureOf = (draft: ReportDesignDraft): string => JSON.stringify(toReportDesignBody(draft));

const DETAILS: DraftTarget = { kind: 'details' };

/** What the API answers to a request about a design that is gone, or no longer the reader's. */
const DESIGN_REFUSED = [403, 404];

/** Whether what a request came back with says it was refused for the design itself. */
const isDesignRefusal = (error: unknown): boolean => {
  const failure = { error };

  return isServerError(failure) && DESIGN_REFUSED.includes(failure.error.status);
};

/** The two ways the page is shown: the canvas it is composed on, and the PDF the server draws of it. */
type View = 'design' | 'pdf';

/**
 * The report designer: the block palette and the design's sections on the left, the page in
 * the middle, the settings of what is selected on the right.
 *
 * The page has two views. Design is a canvas: the cover and closing pages as sheets in HTML,
 * on which blocks are dragged about and dropped in from the palette, and the summary and the
 * checkpoints as what their templates print. It imitates the report and is not it. PDF
 * preview is the report itself, as the server draws it, and the server is asked for it only
 * while that view is the open one.
 *
 * Holds a working copy and is the one writer of a design: the name and the description are
 * part of the draft, and everything is saved as one PUT that claims the version the draft was
 * taken from, so an edit made elsewhere since is a conflict, not an overwrite.
 *
 * An image is sent the moment it is picked, before the draft that names it is saved. Uploads
 * and the save run one after another on one chain, so a save asked for while a file is still
 * on its way waits for it and carries its id.
 */
const ReportDesigner: React.FC<Props> = ({ design, canEdit, reread, closed, onCopy }) => {
  const { t } = useLocalization();
  const { userId } = usePermission();

  const [updateReportDesign] = useUpdateReportDesignMutation();
  const [uploadImage] = useUploadReportDesignImageMutation();
  const [previewDesign] = usePreviewReportDesignMutation();

  // A kept draft is taken up only by whoever left it, and only where it can still be saved.
  const [initial] = useState(() => {
    const kept = canEdit ? keptDrafts.read(userId, design._id) : undefined;
    const draft = draftOf(design);

    return kept ?? { draft, base: { json: signatureOf(draft), version: design.version } };
  });

  // The draft and its base are state for the screen and refs for the chain below: a write
  // that waited its turn reads them as they are when it runs, not as they were when it was asked for.
  const [draft, setDraft] = useState(initial.draft);
  const [base, setBase] = useState<Base>(initial.base);
  const draftRef = useRef(draft);
  const baseRef = useRef(base);

  const [selection, setSelection] = useState<DraftTarget>(DETAILS);
  // Kept for the visit only: the editor opens on the canvas each time.
  const [view, setView] = useState<View>('design');
  // What the canvas was last asked to scroll to.
  const [shown, setShown] = useState<Shown | null>(null);
  // What a press last handed the keyboard on to: one in a block's toolbar, or on one of the inspector's own buttons.
  const [handed, setHanded] = useState<Handover | null>(null);
  // The ids the server has answered for with "no such image".
  const [missing, setMissing] = useState<ReadonlySet<string>>(() => new Set());
  const missingRef = useRef(missing);
  // The files picked during this visit, as addresses a picture can show.
  const [localImages, setLocalImages] = useState<Record<string, string>>({});
  const madeUrls = useRef<string[]>([]);
  const [uploads, setUploads] = useState(0);
  const [saves, setSaves] = useState(0);
  // From the press on Save until that save has answered here, its wait behind an upload included.
  const saving = saves > 0;
  // A save was refused because the design had moved on. Only giving the draft up ends it.
  const [refused, setRefused] = useState(false);
  const refusedRef = useRef(refused);
  // Discard is waiting for the stored design to be read again.
  const [rereading, setRereading] = useState(false);
  const [clearing, setClearing] = useState(false);
  // The page a deletion is being asked about, and what the keyboard's focus is handed on to once it is gone.
  const [deletingPage, setDeletingPage] = useState<{ id: string; after?: Handover } | null>(null);
  const alive = useRef(true);
  const canEditRef = useRef(canEdit);
  const rereadRef = useRef(reread);
  // One of the editor's own writes was refused for the design itself: see `mayWrite`.
  const turnedAway = useRef(false);

  missingRef.current = missing;
  refusedRef.current = refused;
  canEditRef.current = canEdit;
  rereadRef.current = reread;

  const change = useCallback((edit: (current: ReportDesignDraft) => ReportDesignDraft) => {
    draftRef.current = edit(draftRef.current);
    setDraft(draftRef.current);
  }, []);

  const rebase = useCallback((next: Base) => {
    baseRef.current = next;
    setBase(next);
  }, []);

  const signature = useMemo(() => signatureOf(draft), [draft]);
  const dirty = canEdit && signature !== base.json;
  const dirtyRef = useRef(dirty);

  dirtyRef.current = dirty;

  /** Starts again from a copy of the stored design: it is the draft, and the base a save claims. */
  const takeUp = useCallback((stored: ReportDesign) => {
    const fresh = draftOf(stored);

    draftRef.current = fresh;
    setDraft(fresh);
    rebase({ json: signatureOf(fresh), version: stored.version });
    setRefused(false);
  }, [rebase]);

  // Whether a copy can be taken for the design as it is stored now. The one at hand is not
  // always that: the re-read a save sets off waits for every other request, a preview that
  // is being drawn included, and a read that was on its way before a save can land after
  // it. Starting again from such a copy would bring back what the save replaced under a
  // version the server has left, and the next save would be refused as somebody else's.
  const isCurrent = useCallback(
    (copy: ReportDesign) => isCurrentCopy(copy.version, baseRef.current.version, refusedRef.current), [],
  );

  // A newer saved copy, with nothing unsaved here: take it. Unsaved work is never replaced;
  // saving it claims the old version, so the server answers with a conflict instead. A copy
  // that is not the design as it is now is passed over, and the one that is follows it.
  //
  // No copy is judged while a save of the editor's own is on its way. What that save sent is
  // in the design's read, one version up, before the save has answered here and the base has
  // moved to it. Judged in between, the editor's own save would pass for somebody else's,
  // and a draft that had come back to the old base meanwhile, an edit taken back by hand,
  // would be replaced by what was just saved. The copy at hand is judged once the save has
  // ended: the editor's own, or one that somebody else's save brought in the meantime.
  const compared = useRef(design);

  useEffect(() => {
    if (saving || compared.current === design) return;

    compared.current = design;
    if (!dirtyRef.current && isCurrent(design)) takeUp(design);
  }, [design, saving, isCurrent, takeUp]);

  useEffect(() => {
    if (dirty) keptDrafts.keep(userId, design._id, { draft, base });
    else keptDrafts.drop(userId, design._id);
  }, [userId, dirty, draft, base, design._id]);

  // Reloading or closing the tab is the one way a draft is lost, so that asks first: here
  // for the designer on screen, a file on its way included. Once the designer has been left
  // the same is asked for the draft it kept and for the writes it queued, which no effect
  // of its own could do any more.
  const unsettled = dirty || uploads > 0;

  useAskBeforeLeaving(unsettled);

  useEffect(() => {
    const made = madeUrls.current;

    alive.current = true;

    return () => {
      alive.current = false;
      made.forEach((url) => URL.revokeObjectURL(url));
      made.length = 0;
    };
  }, []);

  // ---- Writes: one at a time ----

  // Each PUT claims the version the one before it produced, and an upload has to be through
  // before the save that names its image, so nothing here runs beside anything else.
  const enqueue = useWriteChain(designWrites(design._id));

  // A write that ends after the designer was left has no screen to show its outcome on, and
  // the effect above no longer runs. What it leaves is put where the way back looks: nothing
  // if the draft is saved now, else the draft as it stands, on the base the write left. That
  // is also where the designs list reads its mark from, so the mark follows the write.
  const keepAfterLeaving = useCallback(() => {
    if (alive.current) return;

    const saved = signatureOf(draftRef.current) === baseRef.current.json;

    keptDrafts.leave(userId, design._id, saved ? undefined : { draft: draftRef.current, base: baseRef.current });
  }, [userId, design._id]);

  const reportMissing = useCallback((...imageIds: string[]) => {
    setMissing((current) => (imageIds.every((imageId) => current.has(imageId)) ? current : new Set([...current, ...imageIds])));
  }, []);

  // A request that was refused for the design itself says nothing about the draft: the
  // design is gone, or not the reader's any more. What became of it is the page's to say,
  // from a read of the design, and that read is asked for at once: an upload and a preview
  // set none off, and the one a save sets off waits for every other request.
  const rereadWhereRefused = useCallback((error: unknown) => {
    if (alive.current && isDesignRefusal(error)) void rereadRef.current();
  }, []);

  // The page's answer comes a request and a render after such a refusal, and a save or an
  // upload that waited its turn behind the refused write runs before either. So a write
  // that is refused for the design ends the editor's writes itself, there and then and for
  // the rest of the visit. Only that answer does: a failure that passes (no connection, an
  // error of the server's, too many requests) says nothing about the design, and the next
  // write is sent as before. Nor does a preview's refusal, which can be for the inspection
  // it was to be drawn from.
  const writeRefused = useCallback((error: unknown) => {
    if (isDesignRefusal(error)) turnedAway.current = true;

    rereadWhereRefused(error);
  }, [rereadWhereRefused]);

  /**
   * Whether a save or an upload may be sent, asked when its turn has come: not once the
   * design has turned out to be gone, or no longer the reader's to change, while it waited.
   * Where a write was refused for that and the editor is open all the same, the page's read
   * of the design has not answered yet, or failed. It is asked for again in place of the
   * write, so that the author is told what became of the design, and not left with a Save
   * that sends nothing.
   */
  const mayWrite = useCallback((): boolean => {
    if (!canEditRef.current) return false;
    if (!turnedAway.current) return true;

    if (alive.current) void rereadRef.current();

    return false;
  }, []);

  const upload = useCallback(async (file: File, place: (imageId: string) => void): Promise<boolean> => {
    setUploads((count) => count + 1);

    try {
      return await enqueue(async () => {
        // A file that waited while the design turned out to be gone is not sent after that.
        if (!mayWrite()) return false;

        const result = await uploadImage({ designId: design._id, file });

        // The refusal has been shown by the endpoint: the server's own sentence.
        if (result.error !== undefined || !result.data) {
          writeRefused(result.error);

          return false;
        }

        const { imageId } = result.data;

        if (alive.current) {
          const url = URL.createObjectURL(file);

          madeUrls.current.push(url);
          setLocalImages((current) => ({ ...current, [imageId]: url }));
        }

        // Within this turn of the chain, so that a save waiting behind it finds the image in the draft.
        place(imageId);
        keepAfterLeaving();

        return true;
      });
    } finally {
      setUploads((count) => count - 1);
    }
  }, [enqueue, uploadImage, design._id, keepAfterLeaving, mayWrite, writeRefused]);

  const save = () => {
    setSaves((count) => count + 1);

    void enqueue(async () => {
      const sent = draftRef.current;
      const json = signatureOf(sent);

      // Nothing to send after all (the only change was an upload that failed), or something
      // to fix came up while this waited, the design itself being gone included.
      if (json === baseRef.current.json || draftIssues(sent, missingRef.current).length > 0 || !mayWrite()) return;

      const { version } = baseRef.current;
      const result = await updateReportDesign({ id: design._id, version, ...toReportDesignBody(sent) });

      if (result.error === undefined) {
        // The server raises the version by one, and the next save claims that. The endpoint
        // has put what was sent into the design's read under the same version, so `design`
        // is not the copy from before this save while the re-read is still to come.
        rebase({ json, version: version + 1 });
        // What was typed while the save was on its way is still unsaved, on the new version:
        // the copy this save left in the design's read is not judged before this point.
        keepAfterLeaving();

        return;
      }

      const failure = { error: result.error };

      if (isServerError(failure) && failure.error.status === STALE_VERSION) setRefused(true);

      reportMissing(...missingImagesOf(failure));
      writeRefused(result.error);
    }).finally(() => setSaves((count) => count - 1));
  };

  // Discard goes back to the design as it is stored now. Where the copy at hand is not that
  // (`isCurrent`), the design is read again first, by a request of its own that waits for
  // no other. The draft is given up only once that copy is here: a read that fails leaves
  // the draft, and Discard to be pressed again.
  const discard = async () => {
    if (isCurrent(design)) {
      takeUp(design);

      return;
    }

    setRereading(true);

    try {
      const { data: stored } = await reread();

      if (stored && isCurrent(stored)) {
        takeUp(stored);
        // Asked for and then left: the draft that was kept on the way out is given up as well.
        keepAfterLeaving();
      }
    } finally {
      setRereading(false);
    }
  };

  // ---- What stands in the way ----

  const issues = useMemo(() => (canEdit ? draftIssues(draft, missing) : []), [canEdit, draft, missing]);

  // The stored design has moved past the one this draft was taken from: a save can only be
  // refused. Not said while a save is on its way, whose own copy in the design's read is a
  // version past the base until it has answered here; a save that is refused says it then.
  const outdated = dirty && !saving && (refused || design.version > base.version);
  const busy = saving || uploads > 0 || rereading;
  const canSave = canEdit && (dirty || uploads > 0) && issues.length === 0 && !saving && !outdated && !rereading;

  // ---- Preview ----

  // What a request carries: the draft for whoever may save it, nothing for a reader, who is
  // shown the stored design.
  const content = useMemo(() => normalizeContent(draft), [draft]);
  const live = useMemo(
    () => (canEdit ? JSON.stringify(content) : `stored ${design.version}`), [canEdit, content, design.version],
  );
  const source = usePreviewSource(live);

  const requestPreview = (signal: AbortSignal) => {
    const running = previewDesign({
      id: design._id, draft: canEdit ? content : undefined, source: source.source, locale: source.locale,
    });

    signal.addEventListener('abort', () => running.abort(), { once: true });

    return running.then((result) => {
      // A preview is checked as a save is, so it can be the first to say an image is gone,
      // or the design itself.
      if (result.error !== undefined) {
        reportMissing(...missingImagesOf({ error: result.error }));
        rereadWhereRefused(result.error);
      }

      return toPreviewAnswer(result);
    });
  };

  // ---- Sections, canvas and inspector ----

  // A page or a block can be deleted, or discarded, from under the selection.
  const selected = targetExists(draft, selection) ? selection : DETAILS;

  /**
   * Selects a part from outside the canvas, which then brings it into view. A click on the
   * canvas itself only selects: what was clicked is on screen already. A block that a step
   * in its toolbar has moved may not be, and is pointed at too.
   */
  const point = useCallback((target: DraftTarget) => {
    setSelection(target);
    setShown((current) => ({ target, turn: (current?.turn ?? 0) + 1 }));
  }, []);

  const openView = (next: View) => {
    setView(next);
    // The canvas comes back at the top: it is taken to what the inspector is showing.
    if (next === 'design') point(selected);
  };

  // A drop is one edit of the draft. What was dropped is selected: it is what the author is working on.
  const drop = useCallback((source: DragSource, place: Placement) => {
    const land = (blockId: string, block?: ReportBlock) => {
      change((current) => dropOn(current, source, place, block));
      setSelection({ kind: 'block', id: blockId });
    };

    if (source.kind === 'block') {
      land(source.blockId);
    } else {
      // A palette entry puts down a block made here, as a click on it does.
      const block = createBlock(source.type);

      land(block.id, block);
    }
  }, [change]);

  const putBack = useCallback((blockId: string) => setSelection({ kind: 'block', id: blockId }), []);

  const drag = useBlockDrag({ draft: draftRef, onDrop: drop, onPutBack: putBack });

  const structure: StructureActions = {
    select: point,
    addPage: (section) => {
      const page = newPage();

      change((current) => addPage(current, section, page));
      point({ kind: 'page', id: page.id });
    },
  };

  // Where a click on a palette entry puts its block.
  const paletteTarget = pageForNewBlock(draft, selected);

  /** A block of a type at the end of that page. A design without a page gets a cover page for it first. */
  const addFromPalette = (type: ReportBlockType) => {
    const page = pageForNewBlock(draftRef.current, selected) ?? newPage();

    if (page.blocks.length >= reportDesignLimits.blocksPerPage) return;

    const block = createBlock(type);

    change((current) => insertBlock(locatePage(current, page.id) ? current : addPage(current, 'covers', page), page.id, block));
    point({ kind: 'block', id: block.id });
  };

  /** Puts a copy of a block under it and selects the copy. Answers with the copy's id, or with nothing for a block that is gone. */
  const duplicateBlock = (blockId: string): string | undefined => {
    const at = locateBlock(draftRef.current, blockId);

    if (!at) return undefined;

    const copy = copyOfBlock(at.block);

    change((current) => insertBlock(current, at.page.id, copy, at.index + 1));
    setSelection({ kind: 'block', id: copy.id });

    return copy.id;
  };

  /** Takes a page out of the draft. `after` is what the press that asked for it hands the keyboard's focus on to. */
  const erasePage = (pageId: string, after?: Handover) => {
    change((current) => removePage(current, pageId));
    if (after) setHanded(after);
  };

  /** Deletes a page. An empty one goes at once. One with blocks on it is asked about: there is no undo but Discard. */
  const deletePage = (pageId: string, after?: Handover) => {
    if (locatePage(draftRef.current, pageId)?.page.blocks.length) setDeletingPage({ id: pageId, after });
    else erasePage(pageId, after);
  };

  // The inspector is drawn anew when something else is selected, and three of its own buttons
  // bring that about: Duplicate selects the copy, and a deleted block or page leaves the
  // design's details selected. The pressed button goes with the old inspector and the
  // keyboard's focus with it, so each of the three names a part of the new one to have it
  // (`useHandover`): in the inspector and not on the canvas, which is every sheet away from
  // it in Tab order.
  const inspector: InspectorActions = {
    duplicateBlock: (blockId) => {
      const copyId = duplicateBlock(blockId);

      if (!copyId) return;

      // The same button, drawn for the copy, to be pressed again. Where the copy has filled
      // its page that button is switched off, and the inspector's title has the focus.
      const full = (locateBlock(draftRef.current, copyId)?.page.blocks.length ?? 0) >= reportDesignLimits.blocksPerPage;

      setHanded({ kind: 'inspector', id: full ? 'title' : 'duplicate' });
    },
    removeBlock: (blockId) => {
      change((current) => removeBlock(current, blockId));
      setHanded({ kind: 'inspector', id: 'title' });
    },
    movePage: (pageId, to) => change((current) => movePage(current, pageId, to)),
    removePage: (pageId) => deletePage(pageId, { kind: 'inspector', id: 'title' }),
  };

  // A press in a block's toolbar takes away the button that was pressed, and the keyboard's
  // focus with it: the block is drawn anew on another page, the copy takes the selection, the
  // block is gone. So each of the three below names what has the focus afterwards as well,
  // and this puts it there, as it does for the inspector's three above. A reader who may not
  // edit has no toolbar to press, and finds the inspector's buttons switched off.
  useHandover(handed);

  const canvas: CanvasActions = {
    select: setSelection,
    addPage: structure.addPage,
    movePage: inspector.movePage,
    removePage: (pageId) => deletePage(pageId),
    stepBlock: (blockId, by) => {
      const place = blockStep(draftRef.current, blockId, by);

      if (!place) return;

      change((current) => moveBlockTo(current, blockId, place.pageId, place.index));
      // A step is the author saying "this block", with the mouse as from the keyboard: the
      // canvas keeps the block in view with its toolbar wherever the step has put it, a place
      // along its page or a sheet away, and does not move where the block is in view still.
      point({ kind: 'block', id: blockId });
      // The button that was pressed, on the block where it stands now: the next press takes it a place further.
      setHanded({ kind: 'block', id: blockId, step: by < 0 ? 'up' : 'down' });
    },
    duplicateBlock: (blockId) => {
      const copyId = duplicateBlock(blockId);

      // The copy: it is what is selected now, and what the toolbar is on.
      if (copyId) setHanded({ kind: 'block', id: copyId });
    },
    removeBlock: (blockId) => {
      const at = locateBlock(draftRef.current, blockId);

      if (!at) return;

      // The block that takes its place, or the one before it, or the page by its name when
      // this was its last block. Only the focus goes there. The selection falls back to the
      // design's details as it always did: selected, the next block would bring its toolbar
      // up under the pointer, its Delete where this one was.
      const beside = at.page.blocks[at.index + 1] ?? at.page.blocks[at.index - 1];

      change((current) => removeBlock(current, blockId));
      setHanded(beside ? { kind: 'block', id: beside.id } : { kind: 'page', id: at.page.id });
    },
  };

  const images = useMemo((): DesignImages => ({
    designId: design._id, local: localImages, missing, uploading: uploads > 0, upload, reportMissing,
  }), [design._id, localImages, missing, uploads, upload, reportMissing]);

  const status = () => {
    if (saving) return t('ReportDesign_Editor_Saving');
    if (uploads > 0) return t('ReportDesign_Editor_Uploading');

    return dirty ? t('Customization_Designer_Unsaved') : t('Customization_Designer_Saved');
  };

  return (
    // `clip`, not `hidden`: a hidden overflow is a scroll container, and the inspector's and the
    // preview's `position: sticky` would stick to this box instead of the page.
    <Paper variant="outlined" sx={{ overflow: 'clip' }}>
      {canEdit && (
        <Stack direction="row" sx={toolbarStyles}>
          <Typography variant="caption" color={unsettled ? 'warning.main' : 'text.secondary'}>{status()}</Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small" color="inherit" startIcon={<EraserIcon />} sx={{ textTransform: 'none' }}
            disabled={saving}
            onClick={() => setClearing(true)}
          >
            {t('ReportDesign_Editor_Clear')}
          </Button>
          {/* Not while a file is on its way: its image would be put into the draft Discard has just emptied. */}
          <Button
            size="small" color="inherit" disabled={!dirty || busy} sx={{ textTransform: 'none' }}
            onClick={() => { void discard(); }}
          >
            {t('Customization_Designer_Discard')}
          </Button>
          <Button
            size="small" variant="contained" disableElevation sx={buttonStyles}
            disabled={!canSave}
            onClick={save}
          >
            {t('Common_Save')}
          </Button>
        </Stack>
      )}

      {!canEdit && !closed && (
        <Stack direction="row" sx={bannerStyles}>
          <LockIcon />
          <Box sx={{ flex: 1, minWidth: 0 }}>{t('ReportDesign_Editor_ReadOnly')}</Box>
          {onCopy && (
            <Button size="small" variant="outlined" color="inherit" sx={buttonStyles} onClick={onCopy}>
              {t('ReportDesign_CopyToTeam')}
            </Button>
          )}
        </Stack>
      )}

      {/* The design is gone, or no longer the reader's: what is on screen is all that is left of it here. */}
      {!canEdit && closed && (
        <Stack direction="row" sx={conflictBannerStyles}>
          <ExclamationTriangleIcon />
          <Box sx={{ flex: 1, minWidth: 0 }}>{closed}</Box>
        </Stack>
      )}

      {outdated && (
        <Stack direction="row" sx={conflictBannerStyles}>
          <ExclamationTriangleIcon />
          <Box sx={{ flex: 1, minWidth: 0 }}>{t('ReportDesign_Editor_Conflict')}</Box>
          {/* It promises the design as it is now, so it waits for that copy where it is not here yet. */}
          <Button
            size="small" variant="outlined" color="inherit" sx={buttonStyles} disabled={busy}
            onClick={() => { void discard(); }}
          >
            {t('Customization_Designer_Discard')}
          </Button>
        </Stack>
      )}

      {issues.length > 0 && (
        <Box sx={issueListStyles}>
          <Typography variant="caption" color="text.secondary">{t('Customization_Designer_FixBeforeSaving')}</Typography>
          {issues.map((issue, index) => (
            <ButtonBase key={index} sx={issueLineStyles} onClick={() => point(issue.target)}>
              {`${targetLabel(draft, issue.target, t)}: ${issueWords(issue, t)}`}
            </ButtonBase>
          ))}
        </Box>
      )}

      {/* One drag context over the palette and every sheet of the canvas, so that a block can go from any of them to any page. */}
      <DndContext {...drag.context}>
        <Box sx={layoutStyles}>
          <Box sx={leftCellStyles}>
            {canEdit && (
              <BlockPalette
                // Only the canvas has pages to drop on; over the PDF an entry is clicked.
                draggable={view === 'design'}
                full={(paletteTarget?.blocks.length ?? 0) >= reportDesignLimits.blocksPerPage}
                onAdd={addFromPalette}
              />
            )}
            <StructurePanel draft={draft} selection={selected} canEdit={canEdit} issues={issues} actions={structure} />
          </Box>

          <Box sx={pageCellStyles}>
            <Stack direction="row" sx={viewBarStyles}>
              <ToggleButtonGroup
                exclusive size="small" value={view} sx={segmentedStyles}
                onChange={(_event, next: View | null) => next && openView(next)}
              >
                <ToggleButton value="design">{t('ReportDesign_View_Design')}</ToggleButton>
                <ToggleButton value="pdf">{t('ReportDesign_View_Pdf')}</ToggleButton>
              </ToggleButtonGroup>
              {view === 'design' && (
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
                  {t('ReportDesign_Canvas_Note')}
                </Typography>
              )}
            </Stack>

            <Box sx={viewBodyStyles}>
              {view === 'design' && (
                <DesignCanvas
                  draft={draft}
                  selection={selected}
                  canEdit={canEdit}
                  issues={issues}
                  images={images}
                  carried={drag.carried}
                  placement={drag.placement}
                  shown={shown}
                  actions={canvas}
                />
              )}
              {/*
                Kept for the whole visit and only hidden behind the canvas, where it asks for
                nothing. It is the preview that knows what it has drawn: put away with its tab,
                it called off a request the server went on drawing and asked again on the way
                back, which the server refuses while the first is not done, and it drew a real
                inspection anew on every return, where that waits for Refresh.
              */}
              <Box sx={view === 'pdf' ? viewStyles : [viewStyles, viewHiddenStyles]}>
                <PdfPreview
                  request={requestPreview}
                  revision={source.revision}
                  // A draft the server would refuse is not sent. Its name is not part of a preview.
                  valid={view === 'pdf' && issues.every(isNamingIssue)}
                  stale={source.pending}
                  // Nothing is asked of a design that is gone, and nothing is left to choose
                  // for a drawing that will not be made: the last picture stays as it is.
                  closed={Boolean(closed)}
                  toolbar={<PreviewSource control={source} disabled={Boolean(closed)} />}
                />
              </Box>
            </Box>
          </Box>

          <Paper variant="outlined" sx={settingsCellStyles} key={'id' in selected ? `${selected.kind}:${selected.id}` : selected.kind}>
            <Inspector
              draft={draft}
              selection={selected}
              canEdit={canEdit}
              images={images}
              change={change}
              actions={inspector}
            />
          </Paper>
        </Box>
      </DndContext>

      {/* Both are edits of the draft, which Discard takes back: they ask, but not as a deletion from the server does. */}
      <DesignDialog
        open={deletingPage !== null}
        title={t('ReportDesign_Page_Delete')}
        submitLabel={t('Common_Delete')}
        canSubmit
        saving={false}
        onClose={() => setDeletingPage(null)}
        onSubmit={() => {
          if (deletingPage) erasePage(deletingPage.id, deletingPage.after);
          setDeletingPage(null);
        }}
      >
        <Typography variant="body2">
          {deletingPage ? t('ReportDesign_Page_ConfirmDelete', [pageLabel(draft, deletingPage.id, t)]) : ''}
        </Typography>
      </DesignDialog>

      <DesignDialog
        open={clearing}
        title={t('ReportDesign_Editor_Clear')}
        submitLabel={t('ReportDesign_Editor_Clear')}
        canSubmit
        saving={false}
        onClose={() => setClearing(false)}
        onSubmit={() => {
          change(emptied);
          setClearing(false);
        }}
      >
        <Typography variant="body2">{t('ReportDesign_Editor_ConfirmClear')}</Typography>
      </DesignDialog>
    </Paper>
  );
};

export default ReportDesigner;
